import { FC } from "react";

import { Box, Stack } from "@mui/material";
import { FormType, getField, Locales } from "@simplyk/common";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardDialog } from "@/dashboard-design-system/Dialog/DashboardDialog";
import { Radio } from "@/dashboard-design-system/RadioGroup/Radio";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { ArrowTwoSides } from "@/icons/outlined";

type LocalizedField = { locale: Locales; title?: string };

export type SwapCampaignSupportForm = {
  id: string;
  formType: FormType;
  fields: LocalizedField[] | undefined | null;
};

interface SwapCampaignModalProps {
  open: boolean;
  onClose: () => void;
  supportForms: SwapCampaignSupportForm[];
  locale: Locales;
  selectedReplaceForm: { id: string; formType: FormType } | null;
  onSelectReplaceForm: (form: { id: string; formType: FormType }) => void;
  onConfirm: () => void;
}

export const SwapCampaignModal: FC<SwapCampaignModalProps> = ({
  open,
  onClose,
  supportForms,
  locale,
  selectedReplaceForm,
  onSelectReplaceForm,
  onConfirm,
}) => {
  const { t } = useTranslate();

  return (
    <DashboardDialog
      open={open}
      onClose={onClose}
      title={t("dashboard", "step.advancedSettings.swapCampaignModal.title")}
      icon={<ArrowTwoSides />}
      maxWidth="sm"
      actions={{
        cancel: { onClick: onClose, children: t("dashboard", "common.cancel") },
        main: {
          onClick: onConfirm,
          children: t("dashboard", "step.advancedSettings.swapCampaignModal.confirm"),
          disabled: !selectedReplaceForm,
        },
      }}
    >
      <Stack gap={2}>
        <DashboardTypography variant="body2" vibe="text-neutral-quiet">
          {t("dashboard", "step.advancedSettings.swapCampaignModal.body")}
        </DashboardTypography>
        <Stack gap={2}>
          {supportForms.slice(0, 3).map((sf, index) => {
            const title = getField(sf.fields, locale)?.title ?? sf.id;
            const isSelected = selectedReplaceForm?.id === sf.id;
            return (
              <Box
                key={sf.id}
                onClick={() => onSelectReplaceForm({ id: sf.id, formType: sf.formType })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectReplaceForm({ id: sf.id, formType: sf.formType });
                  }
                }}
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: 2,
                  borderRadius: 1,
                  border: `1px solid ${isSelected ? theme.palette.surface.brand.moderate : theme.palette.border.neutral.quiet}`,
                  cursor: "pointer",
                })}
              >
                <Stack flex={1} minWidth={0}>
                  <DashboardTypography variant="subtitle1" vibe="text-neutral-intense">
                    {title}
                  </DashboardTypography>
                  <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                    {t("dashboard", "step.advancedSettings.swapCampaignModal.campaignSlot", {
                      number: index + 1,
                    })}
                  </DashboardTypography>
                </Stack>
                <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                  <Radio checked={isSelected} value={sf.id} title="" label={<></>} size="medium" />
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Stack>
    </DashboardDialog>
  );
};
